import { useState, useEffect } from 'react';
import {
  Card,
  Layout,
  Text,
  Button,
  Modal,
  FormLayout,
  TextField,
  Box,
  Toast,
  Frame,
  IndexTable,
  DropZone,
  Thumbnail,
} from '@shopify/polaris';

interface TeamMember {
  id: string;
  name: string;
  designation: string;
  email: string;
  phone: string;
  description: string;
  company?: string;
  location?: string;
  website?: string;
  image_url?: string;
}

function TeamMembers() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    email: '',
    phone: '',
    description: '',
    company: '',
    location: '',
    website: '',
    categoryId: '',
    imageUrl: '',
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const API_URL = 'https://ts-team-shopify.anonnaabir.workers.dev';

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team-members`);
      const result = await response.json();
      if (result.success) {
        setMembers(result.data);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team-members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          designation: formData.designation,
          email: formData.email,
          phone: formData.phone,
          description: formData.description,
          company: formData.company,
          location: formData.location,
          website: formData.website,
          image_url: formData.imageUrl,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Team member created successfully');
        setToastActive(true);
        setShowModal(false);
        fetchMembers();
        resetForm();
      } else {
        setToastMessage(result.error || 'Failed to create team member');
        setToastActive(true);
      }
    } catch (error) {
      setToastMessage('Error creating team member');
      setToastActive(true);
    }
  };

  const handleUpdate = async () => {
    if (!editingMember) return;
    try {
      const response = await fetch(`${API_URL}/api/team-members/${editingMember.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          designation: formData.designation,
          email: formData.email,
          phone: formData.phone,
          description: formData.description,
          company: formData.company,
          location: formData.location,
          website: formData.website,
          image_url: formData.imageUrl,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Team member updated successfully');
        setToastActive(true);
        setShowModal(false);
        fetchMembers();
        resetForm();
      } else {
        setToastMessage(result.error || 'Failed to update team member');
        setToastActive(true);
      }
    } catch (error) {
      setToastMessage('Error updating team member');
      setToastActive(true);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;
    try {
      const response = await fetch(`${API_URL}/api/team-members/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Team member deleted successfully');
        setToastActive(true);
        fetchMembers();
      } else {
        setToastMessage(result.error || 'Failed to delete team member');
        setToastActive(true);
      }
    } catch (error) {
      setToastMessage('Error deleting team member');
      setToastActive(true);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      designation: '',
      email: '',
      phone: '',
      description: '',
      company: '',
      location: '',
      website: '',
      categoryId: '',
      imageUrl: '',
    });
    setEditingMember(null);
    setImagePreview(null);
  };

  const openEditModal = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      designation: member.designation || '',
      email: member.email || '',
      phone: member.phone || '',
      description: member.description || '',
      company: member.company || '',
      location: member.location || '',
      website: member.website || '',
      categoryId: '',
      imageUrl: member.image_url || '',
    });
    setImagePreview(member.image_url || null);
    setShowModal(true);
  };

  const handleDropZoneDrop = (_dropFiles: File[], acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        setImagePreview(base64);
        setFormData({ ...formData, imageUrl: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const toastMarkup = toastActive ? (
    <Toast
      content={toastMessage}
      onDismiss={() => setToastActive(false)}
    />
  ) : null;

  const resourceName = {
    singular: 'team member',
    plural: 'team members',
  };

  const rowMarkup = members.map((member, index) => (
    <IndexTable.Row
      id={member.id}
      key={member.id}
      position={index}
    >
      <IndexTable.Cell>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {member.image_url && (
            <Thumbnail
              source={member.image_url}
              alt={member.name}
              size="small"
            />
          )}
          <Text variant="bodyMd" as="span">
            {member.name}
          </Text>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>{member.designation}</IndexTable.Cell>
      <IndexTable.Cell>{member.email}</IndexTable.Cell>
      <IndexTable.Cell>{member.phone}</IndexTable.Cell>
      <IndexTable.Cell>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button size="slim" onClick={() => openEditModal(member)}>
            Edit
          </Button>
          <Button size="slim" variant="primary" onClick={() => handleDelete(member.id)}>
            Delete
          </Button>
        </div>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Frame>
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="400">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text variant="headingMd" as="h2">
                  Team Members
                </Text>
                <Button
                  variant="primary"
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  Add Team Member
                </Button>
              </div>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <IndexTable
              resourceName={resourceName}
              itemCount={members.length}
              headings={[
                { title: 'Name' },
                { title: 'Designation' },
                { title: 'Email' },
                { title: 'Phone' },
                { title: 'Actions' },
              ]}
              selectable={false}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingMember ? 'Edit Team Member' : 'Add Team Member'}
        primaryAction={{
          content: editingMember ? 'Update' : 'Create',
          onAction: editingMember ? handleUpdate : handleCreate,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowModal(false);
              resetForm();
            },
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <div>
              <Text variant="bodyMd" as="p" fontWeight="semibold">
                Profile Photo
              </Text>
              <Box paddingBlockStart="200">
                <DropZone
                  onDrop={handleDropZoneDrop}
                  accept="image/*"
                  type="image"
                  overlayText="Drop image here"
                >
                  {imagePreview ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <Thumbnail
                        source={imagePreview}
                        alt="Preview"
                        size="large"
                      />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <DropZone.FileUpload />
                    </div>
                  )}
                </DropZone>
              </Box>
            </div>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              autoComplete="name"
            />
            <TextField
              label="Designation"
              value={formData.designation}
              onChange={(value) => setFormData({ ...formData, designation: value })}
              autoComplete="organization-title"
            />
            <TextField
              label="Email"
              value={formData.email}
              onChange={(value) => setFormData({ ...formData, email: value })}
              type="email"
              autoComplete="email"
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(value) => setFormData({ ...formData, phone: value })}
              type="tel"
              autoComplete="tel"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              multiline
              autoComplete="off"
            />
            <TextField
              label="Company"
              value={formData.company}
              onChange={(value) => setFormData({ ...formData, company: value })}
              autoComplete="organization"
            />
            <TextField
              label="Location"
              value={formData.location}
              onChange={(value) => setFormData({ ...formData, location: value })}
              autoComplete="address-level2"
            />
            <TextField
              label="Website"
              value={formData.website}
              onChange={(value) => setFormData({ ...formData, website: value })}
              type="url"
              autoComplete="url"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {toastMarkup}
    </Frame>
  );
}

export default TeamMembers;
